import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Anchor,
  Activity,
  ScrollText,
  Settings as Cog,
  Home,
  LogOut,
  Menu,
  X,
  Library,
  Shield,
  Cpu,
  Eye,
  Lock,
  Archive,
  MessageSquare,
  Blocks,
  Search,
  Coins,
  Trophy,
  Flame,
  Zap,
  Database,
  Send,
  HelpCircle,
  Compass,
  Vault,
  CheckCircle2,
  Landmark,
  Radar,
  Network,
  Brain,
  ShieldAlert,
  FileText,
  ChevronDown,
  Hammer,
  Orbit,
  Layers,
  Inbox as InboxIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import useAnchorTapSecret from "../../hooks/useAnchorTapSecret";
import { forumUnreadCount } from "../../lib/api";
import { getArchitectUnreadCount } from "../../lib/architectDm";
import SecurityHud from "./SecurityHud";
import HeartbeatDot from "../HeartbeatDot";
import ArchitectBanner from "../ArchitectBanner";
import { ProbeAlertBanner } from "../ProbeAlertBanner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "../ui/dropdown-menu";

// v19.17 — Grouped navigation.
//
// The nav was 25 flat items in one row → cognitive overload. Now it's
// 7 groups (dropdowns on desktop, collapsible sections on mobile).
// Each item still carries a `tiers` filter — items are hidden when the
// current user's tier isn't listed. Groups auto-collapse to a caret
// button if all their items are tier-filtered out.
//
// Redirects (`/ledger`→/truths, `/bounties`→/truths?bucket=proof,
// `/symbols`→/sky#legend) still resolve, so old LinkedIn / grant deep
// links keep working.
const NAV_GROUPS = [
  {
    id: "operate",
    label: "Operate",
    icon: Activity,
    items: [
      { to: "/submit", label: "Submit a Fact", icon: Send, testid: "nav-submit", tiers: ["free", "builder", "architect"] },
      { to: "/console", label: "Access the Console", icon: Activity, testid: "nav-console", tiers: ["free", "builder", "architect"] },
      { to: "/operate/substrates", label: "Substrates", icon: Compass, testid: "nav-substrates", tiers: ["free", "builder", "architect"] },
    ],
  },
  {
    id: "library",
    label: "Library",
    icon: Network,
    items: [
      { to: "/truths", label: "Truths", icon: ScrollText, testid: "nav-truths", tiers: ["free", "builder", "architect"] },
      { to: "/queue", label: "Verify", icon: CheckCircle2, testid: "nav-verify", tiers: ["free", "builder", "architect"] },
      { to: "/research", label: "Research", icon: Search, testid: "nav-research", tiers: ["free", "builder", "architect"] },
      { to: "/knowledge-graph", label: "Neuro-Symbolic", icon: Network, testid: "nav-knowledge-graph", tiers: ["free", "builder", "architect"] },
    ],
  },
  {
    id: "economy",
    label: "Economy",
    icon: Coins,
    items: [
      { to: "/wallet", label: "Wallet", icon: Coins, testid: "nav-wallet", tiers: ["free", "builder", "architect"] },
      { to: "/leaderboard", label: "Contributors", icon: Trophy, testid: "nav-leaderboard", tiers: ["free", "builder", "architect"] },
      { to: "/reserve", label: "Reserves", icon: Vault, testid: "nav-reserves", tiers: ["free", "builder", "architect"] },
      { to: "/pricing", label: "Pricing", icon: Zap, testid: "nav-pricing", tiers: ["free", "builder", "architect"] },
    ],
  },
  {
    id: "system",
    label: "System",
    icon: Cog,
    items: [
      { to: "/forum", label: "Ask the Architect", icon: MessageSquare, testid: "nav-forum", tiers: ["free", "builder", "architect"], showUnread: true },
      { to: "/inbox", label: "Inbox", icon: InboxIcon, testid: "nav-inbox", tiers: ["free", "builder", "architect"], showInboxUnread: true },
      { to: "/architect/messages", label: "Send Message", icon: Send, testid: "nav-architect-messages", architectOnly: true },
      { to: "/settings", label: "AI Setup", icon: Cog, testid: "nav-settings", tiers: ["free", "builder", "architect"] },
      { action: "logout", label: "Log Out", icon: LogOut, testid: "nav-logout", tiers: ["free", "builder", "architect"], requiresAuth: true },
    ],
  },
  {
    id: "vault",
    label: "Vault",
    icon: Lock,
    architectOnly: true,
    items: [
      { to: "/blackbox", label: "Blackbox", icon: Lock, testid: "nav-blackbox" },
      { to: "/pgrl", label: "PGRL", icon: Eye, testid: "nav-pgrl" },
      { to: "/architect/grants", label: "Grants", icon: Archive, testid: "nav-grants-archive" },
    ],
  },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Mobile drawer — which groups are currently expanded. Initially,
  // the group containing the active route is open; all others closed.
  const [openGroups, setOpenGroups] = useState({});

  // Architect unread count (bell indicator on the "Ask" nav entry).
  const [unreadForum, setUnreadForum] = useState(0);
  useEffect(() => {
    if (!user?.is_architect) return;
    let cancelled = false;
    const tick = () => {
      forumUnreadCount()
        .then((d) => { if (!cancelled) setUnreadForum(d?.unanswered || 0); })
        .catch(() => { /* silent */ });
    };
    tick();
    const iv = setInterval(tick, 60_000); // poll once a minute
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.is_architect]);

  // v20.9 · unread count for Architect → user DMs (badge on Inbox nav)
  const [unreadInbox, setUnreadInbox] = useState(0);
  useEffect(() => {
    if (!user?.user_id || String(user.user_id).startsWith("anon_")) return;
    let cancelled = false;
    const tick = () => {
      getArchitectUnreadCount()
        .then((d) => { if (!cancelled) setUnreadInbox(d?.unread || 0); })
        .catch(() => { /* silent */ });
    };
    tick();
    const iv = setInterval(tick, 45_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.user_id]);

  const isAnonymous = user?.user_id?.startsWith?.("anon_");
  const onAnchorTap = useAnchorTapSecret({
    onUnlock: () => {
      toast.success("Architect entry revealed.");
      navigate("/login");
    },
  });

  // Resolve the caller's tier — gates which nav items render. We
  // fetch /membership/me once on mount; until it lands, default the
  // user to 'free' (most restrictive view) so we never leak architect
  // links during the auth flicker.
  const [tier, setTier] = useState(user?.is_architect ? "architect" : "free");
  useEffect(() => {
    if (user?.is_architect) {
      setTier("architect");
      return;
    }
    let cancelled = false;
    import("../../lib/api").then(({ getMembership }) => {
      getMembership()
        .then((m) => { if (!cancelled) setTier(m?.tier || "free"); })
        .catch(() => { /* keep default 'free' on failure */ });
    });
    return () => { cancelled = true; };
  }, [user?.is_architect, user?.user_id]);

  const allowedByTier = (link) =>
    !link.tiers || link.tiers.includes(tier);

  // Build tier- and role-filtered groups. Groups with zero visible
  // items are dropped entirely so free users don't see empty carets.
  // Also hides `requiresAuth` items (like Log Out) for anonymous visitors.
  const NAV = NAV_GROUPS
    .filter((g) => !g.architectOnly || user?.is_architect)
    .map((g) => ({
      ...g,
      items: g.items
        .filter(allowedByTier)
        .filter((it) => !it.architectOnly || user?.is_architect)
        .filter((it) => !it.requiresAuth || (user && !isAnonymous)),
    }))
    .filter((g) => g.items.length > 0);

  // Auto-open the group whose child matches the current URL when the
  // mobile drawer is first opened. This keeps "where am I" visible.
  const isItemActive = (to) => {
    if (!to) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };
  const activeGroupId = NAV.find((g) => g.items.some((it) => it.to && isItemActive(it.to)))?.id;
  useEffect(() => {
    if (menuOpen && activeGroupId) {
      setOpenGroups((prev) => (prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true }));
    }
  }, [menuOpen, activeGroupId]);

  const toggleGroup = (id) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleLogout = async () => {
    await logout();
    toast.success("Handshake released");
    navigate("/");
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex flex-col">
      <header
        className="border-b border-zinc-800 bg-[#0b0b0d] sticky top-0 z-40"
        data-testid="app-header"
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 gap-3">
          <NavLink
            to="/"
            className="flex items-center gap-3 min-w-0"
            data-testid="nav-home"
          >
            <div
              className="h-8 w-8 shrink-0 rounded-sm border border-zinc-700 flex items-center justify-center relative cursor-pointer select-none"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAnchorTap(); }}
              onTouchStart={(e) => { e.stopPropagation(); onAnchorTap(); }}
              data-testid="anchor-secret-tap"
              aria-hidden="true"
            >
              <Anchor className="h-4 w-4 text-emerald-400 pointer-events-none" />
              <span className="absolute inset-0 rounded-sm animate-sentinel-pulse bg-emerald-500/5" />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-heading text-sm tracking-[0.25em]">
                PROJECTIVY
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center">
                <span>Sentinel · v18.48</span>
                <HeartbeatDot />
              </div>
            </div>
          </NavLink>


          {/* Desktop nav — grouped dropdown menus (v19.17) */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((group) => {
              const GroupIcon = group.icon;
              const groupActive = group.items.some((it) => it.to && isItemActive(it.to));
              const groupUnread =
                group.id === "operate" && unreadForum > 0 ? unreadForum : 0;
              return (
                <DropdownMenu key={group.id}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`nav-group-${group.id}`}
                      className={`relative flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                        groupActive
                          ? "border-emerald-500 text-emerald-400 bg-emerald-500/5"
                          : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"
                      }`}
                    >
                      <GroupIcon className="h-3.5 w-3.5" />
                      {group.label}
                      <ChevronDown className="h-3 w-3 opacity-60" />
                      {groupUnread > 0 && (
                        <span
                          className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-body tabular-nums border border-rose-500 bg-[#09090b] text-rose-300 rounded-sm"
                          data-testid={`nav-group-${group.id}-unread`}
                        >
                          {groupUnread > 99 ? "99+" : groupUnread}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={6}
                    className="min-w-[220px] bg-[#0b0b0d] border border-zinc-800 rounded-sm p-1"
                    data-testid={`nav-group-${group.id}-menu`}
                  >
                    {group.items.map((item) => {
                      const { to, action, label, icon: Icon, testid, showUnread } = item;
                      if (action === "logout") {
                        return (
                          <button
                            key={`action-${testid}`}
                            type="button"
                            onClick={handleLogout}
                            data-testid={testid}
                            className="w-full text-left relative flex items-center gap-3 px-3 py-2 text-xs uppercase tracking-[0.2em] border-l-2 border-transparent text-zinc-400 hover:text-red-400 hover:bg-red-500/5 hover:border-red-500/60 transition-colors"
                          >
                            <Icon className="h-3.5 w-3.5" />
                            <span className="flex-1">{label}</span>
                          </button>
                        );
                      }
                      return (
                        <NavLink
                          key={to}
                          to={to}
                          data-testid={testid}
                          className={({ isActive }) =>
                            `relative flex items-center gap-3 px-3 py-2 text-xs uppercase tracking-[0.2em] border-l-2 transition-colors ${
                              isActive
                                ? "border-emerald-500 text-emerald-400 bg-emerald-500/5"
                                : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60"
                            }`
                          }
                        >
                          <Icon className="h-3.5 w-3.5" />
                          <span className="flex-1">{label}</span>
                          {showUnread && unreadForum > 0 && (
                            <span
                              className="h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-body tabular-nums border border-rose-500 bg-[#09090b] text-rose-300 rounded-sm"
                              data-testid="nav-forum-unread"
                            >
                              {unreadForum > 99 ? "99+" : unreadForum}
                            </span>
                          )}
                          {item.showInboxUnread && unreadInbox > 0 && (
                            <span
                              className="h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-body tabular-nums border border-amber-500 bg-[#09090b] text-amber-300 rounded-sm"
                              data-testid="nav-inbox-unread"
                            >
                              {unreadInbox > 99 ? "99+" : unreadInbox}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
            <NavLink
              to="/"
              data-testid="nav-home-icon"
              className="ml-2 flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] border border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-200"
            >
              <Home className="h-3.5 w-3.5" />
            </NavLink>

            {user && (
              <div className="flex items-center gap-3 ml-4 pl-4 border-l border-zinc-800">
                {!isAnonymous && (
                  <div
                    className="flex items-center gap-2 text-xs"
                    data-testid="user-identity"
                  >
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt=""
                        className="h-7 w-7 border border-zinc-700"
                      />
                    ) : (
                      <div className="h-7 w-7 border border-zinc-700 bg-zinc-900 flex items-center justify-center text-[10px] text-zinc-400">
                        {user.name?.[0] || "?"}
                      </div>
                    )}
                    <div className="leading-tight">
                      <div className="text-zinc-100 text-[11px] max-w-[140px] truncate">
                        {user.name}
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 max-w-[140px] truncate">
                        {user.email}
                      </div>
                    </div>
                    <span
                      data-testid="role-badge"
                      className={`ml-2 px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] border ${
                        user.is_architect
                          ? "border-emerald-500/60 text-emerald-400"
                          : "border-zinc-700 text-zinc-500"
                      }`}
                    >
                      {user.is_architect ? "Architect" : "Operator"}
                    </span>
                  </div>
                )}
                {isAnonymous && (
                  <span
                    data-testid="anon-badge"
                    className="px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-zinc-700 text-zinc-500"
                  >
                    Anonymous
                  </span>
                )}
                {isAnonymous ? null : (
                  <button
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:border-red-500 hover:text-red-400 transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </nav>

          {/* Mobile: avatar + burger */}
          <div className="flex lg:hidden items-center gap-2">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="h-8 w-8 border border-zinc-700"
                data-testid="user-avatar-mobile"
              />
            ) : user ? (
              <div className="h-8 w-8 border border-zinc-700 bg-zinc-900 flex items-center justify-center text-[10px] text-zinc-400">
                {user.name?.[0] || "?"}
              </div>
            ) : null}
            <button
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
              className="p-2 border border-zinc-800 text-zinc-300"
            >
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div
            className="lg:hidden border-t border-zinc-800 bg-[#08080a]"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-3 gap-1">
              {NAV.map((group) => {
                const GroupIcon = group.icon;
                const isOpen = !!openGroups[group.id];
                const groupActive = group.items.some((it) => it.to && isItemActive(it.to));
                const groupUnread =
                  group.id === "operate" && unreadForum > 0 ? unreadForum : 0;
                return (
                  <div key={group.id} className="border border-zinc-800">
                    <button
                      onClick={() => toggleGroup(group.id)}
                      data-testid={`nav-group-${group.id}-mobile`}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-xs uppercase tracking-[0.25em] transition-colors ${
                        groupActive
                          ? "text-emerald-400 bg-emerald-500/5"
                          : "text-zinc-300 hover:bg-zinc-900/60"
                      }`}
                    >
                      <GroupIcon className="h-4 w-4" />
                      <span className="flex-1 text-left">{group.label}</span>
                      {groupUnread > 0 && (
                        <span
                          className="h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-body tabular-nums border border-rose-500 bg-[#09090b] text-rose-300 rounded-sm"
                          data-testid={`nav-group-${group.id}-unread-mobile`}
                        >
                          {groupUnread > 99 ? "99+" : groupUnread}
                        </span>
                      )}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="border-t border-zinc-800 bg-[#050506]">
                        {group.items.map((item) => {
                          const { to, action, label, icon: Icon, testid, showUnread } = item;
                          if (action === "logout") {
                            return (
                              <button
                                key={`action-${testid}`}
                                type="button"
                                onClick={() => { closeMenu(); handleLogout(); }}
                                data-testid={`${testid}-mobile`}
                                className="w-full text-left flex items-center gap-3 pl-8 pr-4 py-3 text-xs uppercase tracking-[0.25em] border-l-2 border-transparent text-zinc-400 hover:text-red-400"
                              >
                                <Icon className="h-4 w-4" />
                                <span className="flex-1">{label}</span>
                              </button>
                            );
                          }
                          return (
                            <NavLink
                              key={to}
                              to={to}
                              onClick={closeMenu}
                              data-testid={`${testid}-mobile`}
                              className={({ isActive }) =>
                                `flex items-center gap-3 pl-8 pr-4 py-3 text-xs uppercase tracking-[0.25em] border-l-2 ${
                                  isActive
                                    ? "border-emerald-500 text-emerald-400 bg-emerald-500/5"
                                    : "border-transparent text-zinc-400"
                                }`
                              }
                            >
                              <Icon className="h-4 w-4" />
                              <span className="flex-1">{label}</span>
                              {showUnread && unreadForum > 0 && (
                                <span
                                  className="h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-body tabular-nums border border-rose-500 bg-[#09090b] text-rose-300 rounded-sm"
                                  data-testid="nav-forum-unread-mobile"
                                >
                                  {unreadForum > 99 ? "99+" : unreadForum}
                                </span>
                              )}
                              {item.showInboxUnread && unreadInbox > 0 && (
                                <span
                                  className="h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-body tabular-nums border border-amber-500 bg-[#09090b] text-amber-300 rounded-sm"
                                  data-testid="nav-inbox-unread-mobile"
                                >
                                  {unreadInbox > 99 ? "99+" : unreadInbox}
                                </span>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              <NavLink
                to="/"
                onClick={closeMenu}
                data-testid="nav-home-mobile"
                className="flex items-center gap-3 px-4 py-3 text-xs uppercase tracking-[0.25em] border border-zinc-800 text-zinc-400"
              >
                <Home className="h-4 w-4" />
                Landing
              </NavLink>
              {user && !isAnonymous && (
                <>
                  <div className="flex items-center gap-3 px-4 py-2 text-xs text-zinc-400 border-t border-zinc-900 mt-2 pt-3">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Signed in
                    </span>
                    <span className="text-zinc-200 truncate">{user.email}</span>
                  </div>
                  <button
                    onClick={() => {
                      closeMenu();
                      handleLogout();
                    }}
                    data-testid="logout-btn-mobile"
                    className="flex items-center gap-3 px-4 py-3 text-xs uppercase tracking-[0.25em] border border-zinc-800 text-red-400"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </>
              )}
              {user && isAnonymous && (
                <div className="flex items-center gap-3 px-4 py-2 text-xs text-zinc-500 border-t border-zinc-900 mt-2 pt-3">
                  <span className="text-[10px] uppercase tracking-[0.25em]">
                    Anonymous session
                  </span>
                </div>
              )}
            </nav>
          </div>
        )}

        <div className="border-t border-zinc-900 bg-[#08080a]">
          <SecurityHud />
        </div>
      </header>

      <ArchitectBanner />
      <ProbeAlertBanner />

      <main className="flex-1">{children}</main>
    </div>
  );
}
